import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";

const WeatherClimate = () => {
  const navigate = useNavigate();

  const seasons = [
    {
      season: "Spring (March - May)",
      icon: "Flower2",
      color: "bg-green-50 border-green-200",
      iconBg: "bg-green-100 text-green-600",
      content: "Spring is one of the best times to visit Nepal. The weather is warm, rhododendrons bloom across the hillsides, and the skies are clear.",
      details: [
        "Temperature: 15-25°C in Kathmandu, 0-15°C in higher elevations",
        "Best for: Trekking, sightseeing, cultural tours",
        "Rhododendron forests in full bloom",
        "Clear mountain views in early morning",
        "Popular for Everest and Annapurna region treks"
      ]
    },
    {
      season: "Summer / Monsoon (June - August)",
      icon: "CloudRain",
      color: "bg-sky-50 border-sky-200",
      iconBg: "bg-sky-100 text-sky-600",
      content: "Monsoon season brings heavy rainfall, especially in the afternoon. While trekking in the rain-shadow areas (Upper Mustang, Dolpo) is still possible, this is the low season for tourism.",
      details: [
        "Temperature: 20-30°C in Kathmandu",
        "Heavy rainfall, particularly in afternoons",
        "Best for: Rain-shadow treks (Upper Mustang, Dolpo)",
        "Lush green landscapes",
        "Fewer tourists - good deals on accommodations",
        "Ropai (rice planting) festival season"
      ]
    },
    {
      season: "Autumn (September - November)",
      icon: "Sun",
      color: "bg-amber-50 border-amber-200",
      iconBg: "bg-amber-100 text-amber-600",
      content: "Autumn is the peak tourist season in Nepal. The weather is crisp and clear, offering the best mountain views of the year. This is the ideal time for trekking and outdoor activities.",
      details: [
        "Temperature: 10-25°C in Kathmandu, -5-15°C at higher elevations",
        "Crystal clear skies and stunning mountain views",
        "Best for: Trekking, peak climbing, mountain flights",
        "Harvest season - terraced fields are golden",
        "Dashain and Tihar festivals celebrate",
        "Busiest time - book well in advance"
      ]
    },
    {
      season: "Winter (December - February)",
      icon: "Snowflake",
      color: "bg-blue-50 border-blue-200",
      iconBg: "bg-blue-100 text-blue-600",
      content: "Winter in Nepal is cold, especially at higher elevations. However, clear skies still offer excellent mountain views. Lower elevation treks and cultural tours are comfortable.",
      details: [
        "Temperature: 2-15°C in Kathmandu, -15-5°C at high elevations",
        "Snowfall possible at higher elevations",
        "Best for: Lower elevation treks, cultural tours, Chitwan safari",
        "Fewer tourists on the trails",
        "Good deals on flights and accommodations",
        "Clear morning skies, clouds often build in afternoon"
      ]
    }
  ];

  const regions = [
    { name: "Kathmandu Valley", temp: "2°C - 30°C", best: "Mar-May, Sep-Nov" },
    { name: "Pokhara & Annapurna", temp: "-5°C - 25°C", best: "Mar-May, Sep-Nov" },
    { name: "Everest Region", temp: "-15°C - 15°C", best: "Mar-May, Sep-Nov" },
    { name: "Chitwan (Terai)", temp: "10°C - 40°C", best: "Oct-Mar" },
    { name: "Upper Mustang", temp: "0°C - 25°C", best: "May-Oct" },
    { name: "Lumbini", temp: "10°C - 35°C", best: "Oct-Mar" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=1600&h=600&fit=crop)" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="section-heading text-white mb-4">Weather & Climate</h1>
              <p className="text-lg text-white/80">Understand Nepal's diverse climate and choose the best time for your visit.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Seasonal overview */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {seasons.map((s, i) => (
                <div key={i} className={`rounded-2xl border p-6 ${s.color}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.iconBg}`}>
                      <Icon name={s.icon} size={18} />
                    </div>
                    <h3 className="font-headline text-gray-900" style={{ fontWeight: 300 }}>{s.season}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{s.content}</p>
                  <ul className="space-y-1">
                    {s.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-gray-600">
                        <Icon name="Check" size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Regional temperature */}
            <h2 className="font-headline text-2xl text-gray-900 mb-6" style={{ fontWeight: 300 }}>Regional Climate Overview</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Region</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Temperature Range</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Best Time to Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-700 font-medium">{r.name}</td>
                      <td className="py-3 px-4 text-gray-600">{r.temp}</td>
                      <td className="py-3 px-4 text-gray-600">{r.best}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-white/50">
          &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default WeatherClimate;
